import {
  Button,
  Icon,
  List,
  ListItem,
  PlainButton,
  PlainLink,
  Popover,
  PopoverContent,
  Truncated,
} from '@customerio/pluma-components/react';
import * as styles from './index.css';
import { useEffect, useState } from 'react';
import { useStore } from '@nanostores/react';
import { currentSessionStore, sessionsStore, updateSessions, changeToSession } from '@customerio/hydra-session-state';
import { useServicesQuery } from '@/services/api';

export function AccountSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const currentSession = useStore(currentSessionStore);
  const allSessions = useStore(sessionsStore);

  const { data, isLoading, error } = useServicesQuery('getSessionMetadata', {});

  useEffect(() => {
    if (isLoading || error != null || data == null) {
      return;
    }

    updateSessions(data);
  }, [data, isLoading, error]);

  const handleSwitchAccount = (index: number) => {
    if (index === currentSession?.id) {
      setIsOpen(false);
      return;
    }

    changeToSession(index);
    setIsOpen(false);
    // Navigate to the dashboard for the new account. Here we don't want the
    // workspace or account ID in the URL, and we also want the page to fully
    // load, rather than navigate internally, so that fresh data is loaded.
    window.location.assign('/');
  };

  if (allSessions.length === 0 || currentSession == null) {
    return null;
  }

  return (
    <Popover isOpen={isOpen} onOpenChange={setIsOpen} placement="bottom-start" withArrow={false}>
      {(context) => (
        <>
          <Button
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
            size="sm"
            variant="subtle"
            className={styles.accountSwitcherButton}
            aria-label={`${currentSession.name} account`}
            aria-description="Click to open account selector."
          >
            <Truncated color="base" text="product-pxs-active">
              {currentSession.name}
            </Truncated>
            <Icon name="chevron-toggle" />
          </Button>

          <PopoverContent className={styles.accountSwitcherContent}>
            <section className={styles.sessionListSection}>
              <List styleType="none">
                {allSessions.map((session) => (
                  <ListItem
                    className={styles.sessionListItem}
                    key={session.email}
                    aria-current={currentSession?.id === session.id}
                  >
                    <PlainButton
                      w="full"
                      px="200"
                      py="150"
                      textAlign="left"
                      onClick={() => handleSwitchAccount(session.id)}
                    >
                      <Truncated display="block" text="product-p-active" aria-label={`${session.name} account`}>
                        {session.name}
                      </Truncated>
                      <Truncated display="block" text="product-pxs" color="subtle">
                        {session.email}
                      </Truncated>
                    </PlainButton>
                  </ListItem>
                ))}
              </List>
            </section>
            {allSessions.length >= 10 ? null : (
              <section className={styles.sessionListSection}>
                <PlainLink
                  className={styles.sessionListItem}
                  px="200"
                  py="150"
                  display="flex"
                  alignItems="center"
                  gap="050"
                  w="full"
                  // Navigating internally to the login page causes issues with
                  // form field tracking. Until we can figure out why, we're
                  // going to do a full reload. Not ideal, but a temporary solution
                  href="/login?addAccount=true"
                  isExternal={true}
                  withSafeExternalAttributes={false}
                >
                  <Icon name="add" />
                  Add another account
                </PlainLink>
              </section>
            )}
          </PopoverContent>
        </>
      )}
    </Popover>
  );
}
